// CB:BEGIN pm-agreement-legal-page
/**
 * CoreBids Property Manager Platform Agreement — standalone public page.
 * Fetches the agreement PDF from GET /api/agreements/pm-platform/pdf.
 * No hardcoded agreement text: live content served from the API.
 */
import React, { useState, useEffect } from 'react';
import SharedHeaderPropertyManager from '../components/SharedHeaderPropertyManager';
import { getApiUrl } from '../lib/getApiUrl';

interface PMAgreementPageProps {
  onNavigate: (screen: string) => void;
}

const PM_AGREEMENT_SECTIONS = [
  { heading: '1. Purpose and Scope', summary: 'Governs access to and use of the CoreBids Platform for property managers.' },
  { heading: '2. Role of CoreBids and Property Manager', summary: 'CoreBids is a marketplace and payment facilitator only. Property managers must have authority to act on behalf of property owners.' },
  { heading: '3. Platform Use and Access', summary: 'Covers account registration, authorized use, and portfolio management obligations.' },
  { heading: '4. Payments, Milestones, and Escrow-Like Processing', summary: 'All payments must go through the CoreBids/Stripe system. Off-platform transactions are prohibited and constitute a material breach.' },
  { heading: '5. Change Orders', summary: 'All scope, price, or timeline changes must be documented in writing before work begins.' },
  { heading: '6. Disputes, Platform Resolution, and Arbitration', summary: '14-day resolution period, then binding arbitration. No class actions.' },
  { heading: '7. Liability, Indemnification, and Disclaimers', summary: 'PMs indemnify CoreBids. CoreBids liability is capped at fees paid in the prior 12 months or $500.' },
  { heading: '8. Compliance, Data Use, and Confidentiality', summary: 'Must comply with all applicable laws. No off-platform solicitation of contractors for 365 days.' },
  { heading: '9. Termination, Suspension, and Survival', summary: 'CoreBids may suspend or terminate for policy violations. Key obligations survive termination.' },
  { heading: '10. Governing Law, Modifications, and E-Signature', summary: 'Governed by North Carolina law. Electronic acceptance is legally binding with timestamp, IP, and user-agent logging.' },
];

const PMAgreementPage: React.FC<PMAgreementPageProps> = ({ onNavigate }) => {
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownload = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(getApiUrl('/api/agreements/pm-platform/pdf'));
      if (!res.ok) throw new Error('Failed to fetch PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CoreBids-Property-Manager-Platform-Agreement-v2.pdf';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      // Fallback: direct link
      window.open(getApiUrl('/api/agreements/pm-platform/pdf'), '_blank', 'noopener');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-agreement" />

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 20px 60px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ display: 'inline-block', background: '#e8f5e9', color: '#1a6b4a', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
            Version 2 · Effective April 9, 2026
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a2530', margin: '0 0 12px' }}>
            Property Manager Platform Agreement
          </h1>
          <p style={{ color: '#64748b', fontSize: 15, margin: '0 0 24px' }}>
            This agreement governs your access to and use of the CoreBids platform.
            By accepting during account setup, you agree to all terms herein.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={handleDownload}
              disabled={pdfLoading}
              style={{ background: '#1a6b4a', color: 'white', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: pdfLoading ? 'not-allowed' : 'pointer', opacity: pdfLoading ? 0.7 : 1 }}
            >
              {pdfLoading ? 'Loading…' : 'Download PDF'}
            </button>
            <button
              onClick={() => onNavigate('pm-dashboard')}
              style={{ background: 'white', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Section summaries */}
        <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '28px 32px', marginBottom: 28 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a2530', margin: '0 0 20px', borderBottom: '2px solid #e8f5e9', paddingBottom: 12 }}>
            Agreement Summary
          </h2>
          <div style={{ display: 'grid', gap: 16 }}>
            {PM_AGREEMENT_SECTIONS.map((s) => (
              <div key={s.heading} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1a6b4a', marginTop: 7, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, color: '#1a2530', fontSize: 14, marginBottom: 2 }}>{s.heading}</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>{s.summary}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key enforcement notices */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 28 }}>
          {[
            { icon: '🔒', title: 'Agreement Required', body: 'You must accept this agreement before creating projects, uploading documents, or communicating with contractors.' },
            { icon: '💳', title: 'Escrow-Only Payments', body: 'All project payments must go through CoreBids/Stripe. Off-platform payments are a material breach subject to liquidated damages.' },
            { icon: '⚖️', title: 'Binding Arbitration', body: 'Disputes unresolved after 14 days go to binding arbitration. No class actions. Prevailing party recovers attorneys\' fees.' },
            { icon: '🚫', title: 'Anti-Circumvention', body: 'You may not solicit CoreBids-introduced contractors off-platform for 365 days after first contact.' },
          ].map((notice) => (
            <div key={notice.title} style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: '18px 20px' }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>{notice.icon}</div>
              <div style={{ fontWeight: 700, color: '#1a2530', fontSize: 14, marginBottom: 6 }}>{notice.title}</div>
              <div style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>{notice.body}</div>
            </div>
          ))}
        </div>

        {/* Full agreement link */}
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#1a6b4a', fontSize: 14 }}>Full Agreement Text</div>
            <div style={{ color: '#166534', fontSize: 13 }}>Download the complete legally binding agreement as a formatted PDF.</div>
          </div>
          <button
            onClick={handleDownload}
            disabled={pdfLoading}
            style={{ background: '#1a6b4a', color: 'white', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: pdfLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: pdfLoading ? 0.7 : 1 }}
          >
            {pdfLoading ? 'Loading…' : 'Download PDF'}
          </button>
        </div>

        {/* Footer note */}
        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, marginTop: 28 }}>
          CoreBids Inc. · Property Manager Platform Agreement · Version 2 · Effective April 9, 2026
          <br />
          Questions? Contact <a href="mailto:support@corebids.com" style={{ color: '#1a6b4a' }}>support@corebids.com</a>
        </p>
      </div>
    </div>
  );
};

export default PMAgreementPage;
// CB:END pm-agreement-legal-page
